import * as H from "history";
import { User } from "oidc-client";
import { NJLOGO } from "./Assets";
import { NavBarBaseProps } from "./components/ResponsiveNavbar";
import { validatorFn, fileListValidatorFn } from "./store";

//#region Interfaces
// tslint:disable-next-line: interface-name
export interface QAPropsBase {
  isRequired: boolean;
  questionContent: string;
  answerPlaceholder?: string;
  answerType: string;
    answerContent: string;
    answerValidator?: validatorFn;
    isAnswered: boolean;
    isAnswerValid: boolean;
    answerValue: any;
    showError?: boolean;
  errorProps?: ErrorProps;
    label?: string;
    min?: number;
    max?: number;
    propName: string;
    answerAdditionalData?: string;
}

export interface BasicLoadingState {
    isLoading: boolean;
    icon?: string;
}

// tslint:disable-next-line: interface-name
export interface OwnershipProps {
  name: string;
  ownerOrMember: number;
  percentageOrVotingStock?: number;
  amount?: number;
}

// tslint:disable-next-line: interface-name
export interface PhysicalAddress {
    street: string;
    state: string;
    city: string;
    zip: string;
}

// tslint:disable-next-line: interface-name
export interface NamesAndLocationsProps {
    name: string;
    street: string;
    state: string;
    city: string;
    zip: string;
}

export interface CoverageProps {
    entityID: string;
    policyEffectiveDate: string;
    entityName: string;
}

export interface ConditionalFileProps {
    answer: string;
    files: FileList | null;
}

// tslint:disable-next-line: interface-name
export interface AppState {
  history: H.History;
  isDarkTheme: boolean;
  isNavbarVisible: boolean;
    optionChosen: PortalType;
    user?: User;
    dropDownOption?: string;
    inquiryType?: number;
}

export enum PortalType {
    None = 0,
    Underwriting = 1,
    SingleEntityOwnership = 2,
    PolicyCoverage = 3,
    ExperienceRating = 4,
    NoticeOfElection = 5,
    Plan = 6,
    Actuarial = 7,
    Membership = 8,
    SpecialCalls = 9
}

// tslint:disable-next-line: interface-name
export interface State {
    appState: AppState;
    answerState: QAPropsBase[] | null;
}

// tslint:disable-next-line: interface-name
export interface StatePackage<T> {
  state: T;
  setState: React.Dispatch<React.SetStateAction<T>>;
}

// tslint:disable-next-line: interface-name
export interface ErrorProps {
  content: string;
  pointing?: "below" | "left" | "right";
}

//#endregion Interfaces

//#region Validators
export const phoneValidator: validatorFn = (s) => s.match(/(\+*[0-9]{0,2} *)(\([0-9]{3}\) *|[0-9]{3}[- ]*)([0-9]{3}[- ]*)([0-9]{4})$/g) != null;
export const dateValidator: validatorFn = (s) => {
    if (s.length < 10 || isNaN(Date.parse(s)))
        return false;
    const date = new Date(s);
    const minDate = new Date('1/1/1753');
    const maxDate = new Date('12/31/9999 23:59:59');
    return date.getTime() > minDate.getTime() && date.getTime() < maxDate.getTime();
}
export const websiteValidator: validatorFn = (s) => s.match(/(http[s]*:\/\/)*([\w-]+(\.[\w-]+){1,})(\/([\w-]*))*/g) != null;
export const emailValidator: validatorFn = (s) => s.match(/^[a-zA-Z0-9.!#$%&�*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/g) != null;
export const fileValidator: validatorFn = (s) => s != null && s.length > 0;
export const conditionalFileValidator: validatorFn = (s) => (s as ConditionalFileProps).answer == "No" || ((s as ConditionalFileProps).files != null && (s as ConditionalFileProps).files!.length > 0);
export function numberValidatorN(n?: number): validatorFn {
  if (n) {
    return (s) => parseInt(s, 10) >= 0 && parseInt(s, 10) <= n;
  } else {
    return (s) => parseInt(s, 10) >= 0;
  }
}
export function stringValidatorN(x: number, y?: number): validatorFn {
  if (y) {
    return (s) => s.length <= (y as number) && s.length > x;
  } else {
    return (s) => s.length > x;
  }
}
export function dropDownValidator(t: string): validatorFn {
    return numberValidatorN(t.split(';').length);
}
export const numberValidator: validatorFn = (s) => s.match(/^[0-9]*$/g) >= 0;
export const noopValidator: validatorFn = (s) => true;
export const ownershipValidator: validatorFn = (s) => {
    let ownerships = s as OwnershipProps[];
    return ownerships.length === 0 ||
        (ownerships.every((o) => o.ownerOrMember === 0 && o.name && o.name.length > 0) ||
            ownerships.every((o) => o.ownerOrMember === 1 && o.name && o.name.length > 0)) &&
        ((ownerships.map((s) => s.amount).reduce((a, c) => (a || 0) + (c || 0)) || 0) <= 100);
}
export const nameLocsValidator: validatorFn = (s) => {
    let nameLocs = s as NamesAndLocationsProps[];
    return nameLocs.length > 0 && nameLocs.every(n => n && n.name.length > 0);
}
export const datesValidator: validatorFn = (s) => {
    let dates = s as string[];
    return dates.length > 0 && dates.every(d => d && dateValidator(d));
}
export const pcovValidator: validatorFn = (s) => (s as CoverageProps).entityID.length > 0 && (s as CoverageProps).entityName.length > 0 && (s as CoverageProps).policyEffectiveDate.length > 0;
//#endregion Validators

//#region State Definitions
export const underwritingQuestionAnswers: QAPropsBase[] =
    [
        {
            answerContent: " : ;Insurer:0;Agent/Broker:1;Employer:2;Other:3",
            answerType: "DropDown", 
            isRequired: true, questionContent: "TYPE OF STAKEHOLDER (Select One)",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "UserTypeSelection"
        },
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of contact" },
             isRequired: true, questionContent: "SUBMITTER'S NAME",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Name"
        },
        {
            answerContent: "", answerPlaceholder: "example@example.com",
            answerType: "email",
            errorProps: { content: "Please enter a valid email address" },
             isRequired: true, questionContent: "SUBMITTER'S EMAIL ADDRESS",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Email"
        },
        {
            answerContent: "", answerPlaceholder: "+1 (999) 999-9999",
            answerType: "tel",
            errorProps: { content: "Please enter a valid phone number" },
             isRequired: true, questionContent: "SUBMITTER'S PHONE NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Phone"
        },
        {
            answerContent: "",
            answerType: "pcov-table",
            isRequired: false, questionContent: "",
            answerValue: {
                entityID: "",
                policyEffectiveDate: "",
                entityName: ""
            }, isAnswerValid: false, isAnswered: false, propName: "EmployerName"
        },
        {
            answerContent: "",
            answerType: "TextArea",
            errorProps: { content: "Please enter a valid description of inquiry" },
             isRequired: true, questionContent: "BRIEF DESCRIPTION OF INQUIRY",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Description"
        },
        {
            answerContent: "",
            answerType: "file",
            isRequired: true, questionContent: "PLEASE UPLOAD A DETAILED LETTER AND ANY SUPPORTING DOCUMENTATION",
            answerValue: "", isAnswerValid: false, isAnswered: false,  propName: ""
        },
    ];

export const planQuestionAnswers: QAPropsBase[] =
    [
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of contact" },
            isRequired: true, questionContent: "SUBMITTER'S NAME",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Name"
        },
        {
            answerContent: "", answerPlaceholder: "example@example.com",
            answerType: "email",
            errorProps: { content: "Please enter a valid email address" },
            isRequired: true, questionContent: "SUBMITTER'S EMAIL ADDRESS",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Email"
        },
        {
            answerContent: "", answerPlaceholder: "+1 (999) 999-9999",
            answerType: "tel",
            errorProps: { content: "Please enter a valid phone number" },
            isRequired: true, questionContent: "SUBMITTER'S PHONE NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Phone"
        },
        {
            answerContent: "",
            answerType: "TextArea",
            errorProps: { content: "Please enter a valid description of inquiry" },
             isRequired: true, questionContent: "BRIEF DESCRIPTION OF CORRESPONDENCE",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Description"
        },
        {
            answerContent: "",
            answerType: "pcov-table-ex",
            isRequired: false, questionContent: "",
            answerValue: {
                entityID: "",
                policyEffectiveDate: "",
                entityName: ""
            }, isAnswerValid: false, isAnswered: false, propName: "EmployerName"
        },
        {
            answerContent: " : ;ANC Notice of Compliance:0;ANC Cancellation Request:1;Plan Relief Request:2;Plan Notification:3",
            answerType: "DropDown",
            isRequired: true, questionContent: "TYPE OF CORRESPONDENCE",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "DocType"
        },
        {
            answerContent: "",
            answerType: "file", 
            isRequired: true, questionContent: "PLEASE UPLOAD CORRESPONDENCE AND ANY SUPPORTING DOCUMENTATION",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: ""
        },
    ];

export const experienceRatingQuestionAnswers: QAPropsBase[] =
    [
        {
            answerContent: " : ;Insurer:0;Agent/Broker:1;Employer:2;Other:3",
            answerType: "DropDown", 
            isRequired: true, questionContent: "TYPE OF STAKEHOLDER (Select One)",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "UserType"
        },
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of contact" },
             isRequired: true, questionContent: "NAME OF CONTACT",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Name"
        },
        {
            answerContent: "", answerPlaceholder: "example@example.com",
            answerType: "email",
            errorProps: { content: "Please enter a valid email address" },
             isRequired: true, questionContent: "EMAIL ADDRESS",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Email"
        },
        {
            answerContent: "", answerPlaceholder: "+1 (999) 999-9999",
            answerType: "tel",
            errorProps: { content: "Please enter a valid phone number" },
             isRequired: true, questionContent: "PHONE NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Phone"
        },
        {
            answerContent: "",
            answerType: "pcov-table",
            isRequired: false, questionContent: "",
            answerValue: {
                entityID: "",
                policyEffectiveDate: "",
                entityName: ""
            }, isAnswerValid: false, isAnswered: false, propName: ""
        },
        {
            answerContent: "",
            answerType: "TextArea",
            errorProps: { content: "Please enter a valid description of inquiry" },
             isRequired: true, questionContent: "BRIEF DESCRIPTION OF INQUIRY",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Description"
        },
        {
            answerContent: "",
            answerType: "file",
            isRequired: true, questionContent: "PLEASE UPLOAD A DETAILED LETTER AND ANY SUPPORTING DOCUMENTATION",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: ""
        },
    ];

export const electionQuestionAnswers: QAPropsBase[] =
    [
        {
            answerContent: " : ;Insurer:0;Agent/Broker:1;Employer:2;Other:3",
            answerType: "DropDown", 
            isRequired: true, questionContent: "TYPE OF STAKEHOLDER (Select One)",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "UserType"
        },
        {
            answerContent: " : ;Large Risk Alternative Rating Option:0;Large Deductible:1;Retro Rating:2",
            answerType: "DropDown", 
            isRequired: true, questionContent: "TYPE OF DOCUMENT (Select One)",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "DocumentType"
        },
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of contact" },
             isRequired: true, questionContent: "NAME OF CONTACT",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Name"
        },
        {
            answerContent: "", answerPlaceholder: "example@example.com",
            answerType: "email",
            errorProps: { content: "Please enter a valid email address" },
             isRequired: true, questionContent: "EMAIL ADDRESS",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Email"
        },
        {
            answerContent: "", answerPlaceholder: "+1 (999) 999-9999",
            answerType: "tel",
            errorProps: { content: "Please enter a valid phone number" },
             isRequired: true, questionContent: "PHONE NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Phone"
        },
        {
            answerContent: "",
            answerType: "TextArea",
            errorProps: { content: "Please enter a valid description of inquiry" },
             isRequired: true, questionContent: "BRIEF DESCRIPTION OF SUBMISSION",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Description"
        },
        {
            answerContent: "",
            answerType: "file", 
            isRequired: true, questionContent: "PLEASE UPLOAD NOTICE OF ELECTION DOCUMENT",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: ""
        },
    ];

export const actuarialQuestionAnswers: QAPropsBase[] =
    [
        {
            answerContent: " : ;Insurer:0;Agent/Broker:1;Employer:2;Reinsurer:4;Other:3",
            answerType: "DropDown", 
            isRequired: true, questionContent: "TYPE OF STAKEHOLDER (Select One)",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "UserType"
        },
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of contact" },
             isRequired: true, questionContent: "NAME OF CONTACT",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Name"
        },
        {
            answerContent: "", answerPlaceholder: "example@example.com",
            answerType: "email",
            errorProps: { content: "Please enter a valid email address" },
             isRequired: true, questionContent: "EMAIL ADDRESS",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Email"
        },
        {
            answerContent: "", answerPlaceholder: "+1 (999) 999-9999",
            answerType: "tel",
            errorProps: { content: "Please enter a valid phone number" },
             isRequired: true, questionContent: "PHONE NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Phone"
        },
        {
            answerContent: "",
            answerType: "text", 
            isRequired: true, questionContent: "COMPANY OR ORGANIZATION",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Company"
        },
        {
            answerContent: "",
            answerType: "TextArea",
            errorProps: { content: "Please enter a valid description of inquiry" },
             isRequired: true, questionContent: "BRIEF DESCRIPTION OF SUBMISSION",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Description"
        },
        {
            answerContent: "Yes;No",
            answerType: "conditional-file",
            isRequired: false, questionContent: "DO YOU NEED TO UPLOAD DOCUMENTATION?",
            answerValue: "", isAnswerValid: true, isAnswered: false, propName: ""
        },
    ];

export const membershipQuestionAnswers: QAPropsBase[] =
    [
        {
            answerContent: " : ;New Carrier:0;Participating/Dividends:1;Managed Care Program:2;Premium Discount:3;Forms & Endorsements:4;Contact Change:5;Other Inquiries:6",
            answerType: "DropDown", 
            isRequired: true, questionContent: "TYPE OF INQUIRY",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "DocType"
        },
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of contact" },
             isRequired: true, questionContent: "NAME OF CONTACT",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Name"
        },
        {
            answerContent: "", answerPlaceholder: "example@example.com",
            answerType: "email",
            errorProps: { content: "Please enter a valid email address" },
             isRequired: true, questionContent: "EMAIL ADDRESS",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Email"
        },
        {
            answerContent: "", answerPlaceholder: "+1 (999) 999-9999",
            answerType: "tel",
            errorProps: { content: "Please enter a valid phone number" },
             isRequired: true, questionContent: "PHONE NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Phone"
        },
        {
            answerContent: "",
            answerType: "text", 
            isRequired: true, questionContent: "COMPANY OR ORGANIZATION",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Company"
        },
        {
            answerContent: "",
            answerType: "number",
            errorProps: { content: "Please enter a valid company number" },
            isRequired: true, questionContent: "COMPANY NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "CompanyNum"
        },
        {
            answerContent: "",
            answerType: "TextArea",
            errorProps: { content: "Please enter a valid description of inquiry" },
            isRequired: true, questionContent: "BRIEF DESCRIPTION OF SUBMISSION",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Description"
        },
        {
            answerContent: "Yes;No",
            answerType: "conditional-file",
            isRequired: false, questionContent: "DO YOU NEED TO UPLOAD DOCUMENTATION?",
            answerValue: "", isAnswerValid: true, isAnswered: false, propName: ""
        },
    ];

export const specialCallsQuestionAnswers: QAPropsBase[] =
    [
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of contact" },
             isRequired: true, questionContent: "NAME OF CONTACT",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Name"
        },
        {
            answerContent: "", answerPlaceholder: "example@example.com",
            answerType: "email",
            errorProps: { content: "Please enter a valid email address" },
             isRequired: true, questionContent: "EMAIL ADDRESS",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Email"
        },
        {
            answerContent: "", answerPlaceholder: "+1 (999) 999-9999",
            answerType: "tel",
            errorProps: { content: "Please enter a valid phone number" },
             isRequired: true, questionContent: "PHONE NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Phone"
        },
        {
            answerContent: "Yes;No",
            answerType: "conditional-file", answerAdditionalData: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;true",
            isRequired: true, questionContent: "DO YOU NEED TO UPLOAD DATA FOR CALL 9887?",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Files1"
        },
        {
            answerContent: "Yes;No",
            answerType: "conditional-file", answerAdditionalData: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;true",
            isRequired: true, questionContent: "DO YOU NEED TO UPLOAD DATA FOR CALL 0942?",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Files2"
        },
        {
            answerContent: "Yes;No",
            answerType: "conditional-file", answerAdditionalData: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;true",
            isRequired: true, questionContent: "DO YOU NEED TO UPLOAD DATA FOR CALL 0012?",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Files3"
        },
    ];

export const singleEntityQuestionAnswers: QAPropsBase[] =
    [
        {
            answerContent: " : ;Insurer:0;Agent/Broker:1;Employer:2;Other:3",
            answerType: "DropDown", 
            isRequired: true, questionContent: "TYPE OF STAKEHOLDER (Select One)",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "UserType"
        },
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of contact" },
             isRequired: true, questionContent: "NAME OF CONTACT",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Title"
        },
        {
            answerContent: "", answerPlaceholder: "+1 (999) 999-9999",
            answerType: "tel",
            errorProps: { content: "Please enter a valid phone number" },
             isRequired: true, questionContent: "PHONE NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Phone"
        },
        {
            answerContent: "", answerPlaceholder: "example@example.com",
            answerType: "email",
            errorProps: { content: "Please enter a valid email address" },
             isRequired: true, questionContent: "EMAIL ADDRESS",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Email"
        },
        {
            answerContent: "",
            answerType: "text", answerValidator: websiteValidator,
            errorProps: { content: "Please enter a valid website" }, 
            isRequired: false, questionContent: "WEBSITE",
            answerValue: "", isAnswerValid: true, isAnswered: false, propName: "Website"
        },
        {
            answerContent: "",
            answerType: "pcov-table",
            isRequired: false, questionContent: "",
            answerValue: {
                entityID: "",
                policyEffectiveDate: "",
                entityName: ""
            }, isAnswerValid: false, isAnswered: false, propName: ""
        },
        {
            answerContent: "",
            answerType: "ownership-table",
            isRequired: false, questionContent: "",
            answerValue: [], isAnswerValid: false, isAnswered: false, propName: "SingleEntityOwnership"
        },
        {
            answerContent: "",
            answerType: "date", 
            errorProps: { content: "Please enter valid date in MM/dd/yyyy.\r\nFor date unknown: use current date." },
            isRequired: true, questionContent: "ON WHAT DATE DID CURRENT OWNERSHIP COME INTO EXISTENCE?",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "OwnershipEffDate"
        },
        {
            answerContent: "Yes;No",
            answerType: "Radio", 
            isRequired: true, questionContent: "IS ENTITY RELATED TO ANY OTHER NJ ENTITY?",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "N/A"
        },
        {
            answerContent: "",
            answerType: "text",
            isRequired: false, questionContent: "NAME OF OTHER ENTITY",
            answerValue: "", isAnswerValid: true, isAnswered: false, propName: "OtherEntityName"
        },
        {
            answerContent: "Yes;No",
            answerType: "conditional-file",
            isRequired: false, questionContent: "DO YOU NEED TO UPLOAD DOCUMENTATION?",
            answerValue: "", isAnswerValid: true, isAnswered: false, propName: ""
        },
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of submitter" },
            isRequired: true, questionContent: "YOUR NAME AND TITLE",
            answerValue: "", isAnswerValid: true, isAnswered: false, propName: "Name"
        },
        {
            answerContent: "", answerType: "Checkbox",
            isRequired: true, label: "I CERTIFY THAT THE INFORMATION CONTAINED IN THIS FORM IS COMPLETE AND CORRECT",
            questionContent: "ATTESTATION",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "N/A"
        },
    ];

export const policyCoverageQuestionAnswers: QAPropsBase[] =
    [
        {
            answerContent: " : ;Insurer:0;Agent/Broker:1;Employer:2;Other:3",
            answerType: "DropDown",
             isRequired: true, questionContent: "TYPE OF STAKEHOLDER (Select One)",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "UserType"
        },
        {
            answerContent: "",
            answerType: "text",
            errorProps: { content: "Please enter a valid name of contact" },
             isRequired: true, questionContent: "NAME OF CONTACT",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Name"
        },
        {
            answerContent: "", answerPlaceholder: "example@example.com",
            answerType: "email",
            errorProps: { content: "Please enter a valid email address" },
             isRequired: true, questionContent: "EMAIL ADDRESS",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Email"
        },
        {
            answerContent: "", answerPlaceholder: "+1 (999) 999-9999",
            answerType: "tel",
            errorProps: { content: "Please enter a valid phone number" },
             isRequired: true, questionContent: "PHONE NUMBER",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Phone"
        },
        {
            answerContent: "",
            answerType: "TextArea",
            errorProps: { content: "Please enter a valid description of inquiry" },
             isRequired: true, questionContent: "DESCRIPTION OF INQUIRY",
            answerValue: "", isAnswerValid: false, isAnswered: false, propName: "Description"
        },
        {
            answerContent: "",
            answerType: "date-table",
            isRequired: false, questionContent: "",
            answerValue: [], isAnswerValid: false, isAnswered: false, propName: "PolicyCoverageDates"
        },
        {
            answerContent: "",
            answerType: "names-locations-table",
            isRequired: false, questionContent: "",
            answerValue: [], isAnswerValid: false, isAnswered: false, propName: "PolicyCoverageNameLocs"
        },
        {
            answerContent: "Yes;No",
            answerType: "conditional-file",
            isRequired: false, questionContent: "DO YOU NEED TO UPLOAD DOCUMENTATION?",
            answerValue: "", isAnswerValid: true, isAnswered: false, propName: ""
        },
    ];


export const navbarSettings: NavBarBaseProps = {
    leftActions: [],
    leftItems: [],
    logoSrc: NJLOGO,
    rightActions: [],
    rightItems: [
        { to: "/audit-inquiry", content: "Audit Inquiry", key: "audit" },
        { to: "/class-inquiry", content: "Class Inquiry", key: "class" },
        { to: "/single-entity", content: "Single Entity Ownership", key: "single-entity" },
        { to: "/policy-coverage", content: "Policy Coverage Request", key: "policy-coverage" },
        { to: "/experience-rating", content: "Experience Rating Inquiry", key: "experience-rating" },
        { to: "/notice-of-election", content: "Notice Of Election", key: "notice-of-election" },
        { to: "/plan", content: "Plan Correspondence", key: "plan" },
        { to: "/actuarial", content: "Actuarial Inquiry", key: "actuarial" },
        { to: "/special-calls", content: "Special Call Submission", key: "special-calls" },
        { to: "/membership", content: "Membership Inquiry", key: "membership" },
    ],
    title: "Workers Comp Inquiry Portal",
};

export const UNDERWRITING_INITIAL_STATE = {
    questionAnswerState: underwritingQuestionAnswers,
};

export const PLAN_INITIAL_STATE = {
    questionAnswerState: planQuestionAnswers,
};

export const ACTUARIAL_INITIAL_STATE = {
    questionAnswerState: actuarialQuestionAnswers,
};

export const MEMBERSHIP_INITIAL_STATE = {
    questionAnswerState: membershipQuestionAnswers,
};

export const EXPERIENCE_RATING_INITIAL_STATE = {
    questionAnswerState: experienceRatingQuestionAnswers,
};

export const NOTICE_OF_ELECTION_INITIAL_STATE = {
    questionAnswerState: electionQuestionAnswers,
};

export const OWNERSHIP_INITIAL_STATE = {
    questionAnswerState: singleEntityQuestionAnswers,
};

export const COVERAGE_INITIAL_STATE = {
    questionAnswerState: policyCoverageQuestionAnswers,
};

export const SPECIAL_CALLS_INITIAL_STATE = {
    questionAnswerState: specialCallsQuestionAnswers,
};

export const HOME_INITIAL_STATE = null;

export const APP_INITIAL_STATE = {
  history: H.createBrowserHistory(),
  privileges: "",
  isDarkTheme: false,
    isNavbarVisible: false,
    optionChosen: PortalType.None
};

export const GLOBAL_INITIAL_STATE = {
  appState: APP_INITIAL_STATE,
  answerState: HOME_INITIAL_STATE,
};
//#endregion State Definitions
